import { Box, Divider, ScrollArea, Stack, Text } from '@mantine/core';
import { isDark, useReplaceQueryParams } from '@/utils/utils';

import { API_ENDPOINT } from '@/config/endpoint';
import { BadgeButton } from '@/components/Badge';
import BookmarkButton from '@/components/Buttons/BookmarkButton';
import { COMPANY_LOGO_ENDPOINT } from '@/config/endpoint';
import { IArchitectureDetail } from '@/types/architecture';
import Image from 'next/image';
import Link from 'next/link';
import TechstackAvatarGroup from '@/components/TechstackAvatarGroup';
import Toast from '@/components/Toast';
import { archSearchKeyword } from '@/features/Architecture/architecture.state';
import { imageOpt } from '@/utils/images';
import { memo } from 'react';
import { messages } from '@/consts/Messages';
import themeVariables from '@/styles/themeVariables';
import { useArchitectureCardStyle } from '../../architecture.style';
import { useGetFavoriteArchitecture } from '@/features/Architecture/hook/api';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';

const checkFavorite = (id) => {
  const { data: favoriteList, refetch } = useGetFavoriteArchitecture();

  const setFavoriteList = new Set(favoriteList);

  const handleBookmark = async () => {
    try {
      setFavoriteList.has(id)
        ? await API_ENDPOINT.architecture
            .deleteFavoriteArchitectureItem(id)
            .then(() => Toast.success(messages.BOOKMARK_DELETED_SUCCESSFUL))
        : await API_ENDPOINT.architecture
            .addFavoriteArchitectureItem(id)
            .then(() => Toast.success(messages.BOOKMARK_ADDED_SUCCESSFUL));
    } catch (err) {
      Toast.error(messages.UNKNOWN_ERROR);
    } finally {
      refetch();
    }
  };

  return { setFavoriteList, handleBookmark };
};

const ArchitectureCard = ({
  architecture: {
    id,
    reference_company_id,
    reference_company_name,
    title,
    techstack_list,
    service_category_list,
    img_url,
  },
}: {
  architecture: IArchitectureDetail;
  border?: boolean;
}) => {
  const router = useRouter();

  const { dark } = isDark();
  const { classes } = useArchitectureCardStyle({ dark });
  const { setFavoriteList, handleBookmark } = checkFavorite(id);
  const { urlSearchParams, setQueryParams } = useReplaceQueryParams();

  const handleCategory = (category: string) => {
    router.push(`/architecture/list?category=${category}`);
  };

  const setKeyword = useSetAtom(archSearchKeyword);

  const handleLogo = (e) => {
    e.stopPropagation();

    setKeyword((prev) => ({ ...prev, company: reference_company_id + '' }));

    urlSearchParams.set('company', reference_company_id + '');
    setQueryParams(urlSearchParams);
  };

  return (
    <Box
      p={24}
      sx={{
        border: `1.2px solid ${themeVariables.lineDark.neutral(dark)}`,
        borderRadius: '16px',
      }}
    >
      <div className={classes.headerContainer} onClick={handleLogo}>
        <Image
          width={50}
          height={50}
          className={classes.headerCompanyLogo}
          src={`${COMPANY_LOGO_ENDPOINT}/${reference_company_id}.png`}
          alt="logo"
          style={{ backgroundColor: '#fff' }}
        />
        <Text className={classes.headerCompanyTitle}>
          {reference_company_name}
        </Text>
        <Box m={'0 4px 0 auto'}>
          <BookmarkButton
            size={24}
            active={setFavoriteList.has(id)}
            onClick={handleBookmark}
          />
        </Box>
      </div>
      <Divider my={10} color={themeVariables.lineDark.light200(dark)} />
      <Link href={`/architecture/detail/${id}`}>
        <Stack pos={'relative'} h={200} my={8}>
          <Image
            fill
            sizes="100vw"
            className={classes.img}
            src={imageOpt(img_url, 800)}
            alt="thumbnail"
          />
        </Stack>
        <Text lineClamp={2} className={classes.title}>
          {title}
        </Text>
        {techstack_list && techstack_list.length > 0 ? (
          <div className={classes.avatarGroupContainer}>
            <TechstackAvatarGroup techstackIds={techstack_list} size={26} />
          </div>
        ) : (
          <div className={classes.emptyAvatarGroupContainer}>
            * 관련 기술 스택이 없습니다 🙄
          </div>
        )}

        <ScrollArea type={'scroll'} scrollbarSize={4}>
          <div className={classes.tagGroupContainer}>
            {service_category_list?.map((category) => (
              <BadgeButton
                key={category.category_name}
                text={category.category_name}
                onClick={() => {
                  handleCategory(category.category_name);
                }}
              />
            ))}
          </div>
        </ScrollArea>
      </Link>
    </Box>
  );
};

export default memo(ArchitectureCard);
